import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  Renderer2,
  viewChild,
  viewChildren,
} from '@angular/core';
import type { FormArray, FormGroup } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';

import type { Observable } from 'rxjs';

import { UserSelectContainerComponent } from '@core/account-ui';
import { CustomFieldFormGroup } from '@core/custom-fields-types';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import { FixedSelectOptionsHelper } from '@core/offers-logic';
import type { SelectOption } from '@core/user-interface-legacy';
import {
  BoxComponent,
  FormFieldComponent,
  HeadingComponent,
  IconComponent,
  IconHelpComponent,
  LabelComponent,
  NumericInputComponent,
  RtTooltip,
  SelectComponent,
} from '@core/user-interface-legacy';
import { CurrencySelectFieldComponent } from '@recruitee/currency-ui';
import { CustomFieldComponent, CustomFieldViewContext } from '@recruitee/custom-fields-ui';
import { RequisitionOnlyIconComponent } from '@recruitee/requisitions-ui';

import type { RequisitionForm, AvailableFieldsMap, FormBox } from '../../../requisitions.types';
import { RequisitionFormManagerService } from '../../../services/requisition-form-manager.service';
import { DynamicSelectFieldComponent } from '../../form-elements/dynamic-select-field/dynamic-select-field.component';
import { SalaryFormGroupComponent } from '../../form-elements/salary-form-group/salary-form-group.component';

type FixedSelectOptions = {
  categories: SelectOption[];
  educations: SelectOption[];
  employmentTypes: SelectOption[];
  experiences: SelectOption[];
  salaryPeriods: SelectOption[];
  priorities: SelectOption[];
};

@Component({
  selector: 'rt-requisitions-details-form',
  templateUrl: './requisitions-details-form.component.html',
  styleUrls: ['./requisitions-details-form.component.less'],
  imports: [
    BoxComponent,
    FormFieldComponent,
    HeadingComponent,
    IconHelpComponent,
    LabelComponent,
    RtTooltip,
    SalaryFormGroupComponent,
    SelectComponent,
    TranslatePipe,
    ReactiveFormsModule,
    NumericInputComponent,
    UserSelectContainerComponent,
    CustomFieldComponent,
    CurrencySelectFieldComponent,
    DynamicSelectFieldComponent,
    IconComponent,
    RequisitionOnlyIconComponent,
  ],
})
export class RequisitionsDetailsFormComponent {
  private readonly fixedSelectOptions = inject(FixedSelectOptionsHelper);
  private readonly i18n = inject(I18nService);
  private readonly renderer = inject(Renderer2);

  protected readonly CustomFieldViewContext = CustomFieldViewContext;

  public readonly requisitionFormManager = inject(RequisitionFormManagerService);

  public readonly form = input<FormGroup<RequisitionForm>>(undefined!);
  public readonly availableDetailsFields = input<AvailableFieldsMap[FormBox.Details]>(undefined!);
  public readonly departmentSelectOptions = input<SelectOption[]>(undefined!);
  public readonly isFormEditable = input<boolean>(undefined!);
  public readonly isDepartmentsMode = input<boolean>(undefined!);

  public defaultFields = viewChildren('defaultField', { read: ElementRef<FormFieldComponent> });
  public salaryGroup = viewChild(SalaryFormGroupComponent);
  public customFields = viewChildren(CustomFieldComponent);

  public allFields = computed(() => {
    const defaultFields = this.defaultFields();
    const salaryFields = this.salaryGroup() ? [...this.salaryGroup()!.fields()] : [];
    const customFieldFields = this.customFields().flatMap(customField => customField.fields());
    return [...defaultFields, ...salaryFields, ...customFieldFields];
  });

  public addFieldClassesEffect = effect(() => {
    const allFields = this.allFields();
    allFields.forEach((field, index) => {
      if (index % 3 === 2) {
        this.renderer.addClass(field.nativeElement, 'field-right');
      } else {
        this.renderer.removeClass(field.nativeElement, 'field-right');
      }
    });
  });

  public selectOptions: FixedSelectOptions = {
    employmentTypes: this.fixedSelectOptions.getEmploymentTypeSelectOptions(),
    educations: this.fixedSelectOptions.getEducationSelectOptions(),
    experiences: this.fixedSelectOptions.getExperienceSelectOptions(),
    categories: this.fixedSelectOptions.getCategorySelectOptions(),
    salaryPeriods: this.fixedSelectOptions.getSalaryPeriodSelectOptions(),
    priorities: this.fixedSelectOptions.getPrioritySelectOptions(),
  };

  public formFieldsConfig = {
    department: {
      label: this.i18n.translate('app.offer_form.department'),
      searchPlaceholder: this.i18n.translate('app.common.placeholders.search.departments.other'),
      formControlName: 'departmentId',
    },
  };

  public get requisitionUpdated$(): Observable<void> {
    return this.requisitionFormManager.updated$;
  }

  public setUserSelectFieldValue(fieldName: string, value: number | null) {
    this.form().get(fieldName)?.setValue(value);
    this.form().markAsDirty();
  }

  public hasFormGroupAllFieldsFilled(formGroup?: FormGroup): boolean {
    if (!formGroup) return false;
    return Object.values(formGroup.controls).every(control => control.value);
  }

  public get customFieldsFormArray(): FormArray<CustomFieldFormGroup> | undefined {
    return this.form().controls.customFields;
  }
}
